import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, X, Music, Mic2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CartPage = () => {
  const { cart, removeFromCart, clearCart, user } = useApp();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const total = cart.reduce((sum, item) => sum + item.price, 0);

  const handleCheckout = async (paymentMethod) => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout`, {
        items: cart,
        payment_method: paymentMethod,
        origin_url: window.location.origin
      }, { withCredentials: true });

      if (paymentMethod === 'stripe') {
        window.location.href = response.data.checkout_url;
      } else {
        clearCart();
        navigate('/payment/etransfer', { state: response.data });
      }
    } catch (e) {
      toast.error('Checkout failed. Please try again.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-32 px-6 lg:px-12" data-testid="cart-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-white" style={{ fontFamily: 'Syne' }}>
          Your <span className="gradient-text">Cart</span>
        </h1>

        {cart.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingCart size={48} className="mx-auto mb-4 text-[#D4AF37]/30" />
            <p className="text-neutral-500 mb-6">Your cart is empty</p>
            <button onClick={() => navigate('/beats')} className="btn-primary">
              Browse Beats
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-8">
              {cart.map((item, index) => (
                <div key={`${item.item_id}-${item.license_type}-${index}`} className="glass-card p-4 flex items-center justify-between" data-testid={`cart-item-${index}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                      {item.item_type === 'beat' ? <Music size={20} className="text-[#D4AF37]" /> : <Mic2 size={20} className="text-[#FFD700]" />}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-sm text-neutral-500 capitalize">{item.item_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="font-bold text-lg gradient-text">${item.price} CAD</span>
                    <button 
                      onClick={() => removeFromCart(item.item_id, item.license_type)}
                      className="p-2 text-neutral-500 hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${index}`}
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="glass-card p-6 mb-8">
              <div className="flex items-center justify-between text-lg mb-6">
                <span className="text-neutral-400">Total</span>
                <div className="text-right">
                  <span className="text-2xl font-bold gradient-text">${total.toFixed(2)}</span>
                  <span className="text-neutral-500 ml-2">CAD</span>
                </div>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => handleCheckout('stripe')}
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="checkout-stripe-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Pay with Card</>
                  )}
                </button>
                <button
                  onClick={() => handleCheckout('etransfer')}
                  disabled={loading}
                  className="btn-secondary w-full"
                  data-testid="checkout-etransfer-btn"
                >
                  Pay with E-Transfer
                </button>
              </div>
            </div>

            <button onClick={clearCart} className="text-neutral-500 hover:text-[#D4AF37] text-sm transition-colors">
              Clear Cart
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CartPage;
